import type {
  CloudinaryAsset,
  SanityImage,
} from '@bulknutrients/bulk-shared-lib/interfaces/Sanity';
import { mergeStyles } from '@bulknutrients/bulk-shared-lib/utils';
import { BulkErrorBoundary } from '@components/Alerts/ErrorBoundaryFallback';
import { Icons } from '@components/Icons/Icons';
import { CustomImage } from '@components/Media/Image';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useRef, useState } from 'react';

export function ImageGallerySection({
  images,
}: {
  images: (SanityImage | CloudinaryAsset)[];
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [centerTrack, setCenterTrack] = useState(false);

  const trackRef = useRef<HTMLDivElement>(null);
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({
      playOnInit: true,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
      delay: 5000,
    }),
  ]);
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  });

  const measure = useCallback(() => {
    if (!trackRef.current || !emblaThumbsApi) return;

    const viewportWidth = emblaThumbsApi.rootNode().clientWidth;
    const trackWidth = trackRef?.current.scrollWidth;
    // catch edge case where emblaThumbsApi is initialized but the trackRef is not yet rendered
    // seems to happen when screen size jumps quickly
    if (viewportWidth === 0 || trackWidth === 0) return;

    setCenterTrack(trackWidth <= viewportWidth);
  }, [emblaThumbsApi]);

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi || !emblaThumbsApi) return;
      // reset the autoplay timer when a thumbnail is clicked to avoid immediate slide change
      emblaMainApi.plugins().autoplay?.reset();
      emblaMainApi.scrollTo(index);
    },
    [emblaMainApi, emblaThumbsApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return;

    const newIndex = emblaMainApi.selectedScrollSnap();

    setSelectedIndex(newIndex);
    emblaThumbsApi.scrollTo(newIndex);
  }, [emblaMainApi, emblaThumbsApi, setSelectedIndex]);

  const scrollPrev = useCallback(() => {
    if (emblaMainApi) emblaMainApi.scrollPrev();
  }, [emblaMainApi]);

  const scrollNext = useCallback(() => {
    if (emblaMainApi) emblaMainApi.scrollNext();
  }, [emblaMainApi]);

  useEffect(() => {
    if (!emblaMainApi) return;
    // ensure both carousels are snapping to same index
    onSelect();
    emblaMainApi.on('select', onSelect).on('reInit', onSelect);
  }, [emblaMainApi, onSelect]);

  useEffect(() => {
    // Measure the track width and determine if it should be centered
    // This is because justify-center on track container causes alignement issues when looping is enabled
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  useEffect(() => {
    if (!emblaMainApi || !emblaThumbsApi) return;
    // stop and start autoplay of hero carousel based on interactions in thumbnail carousel
    // not ideal if user is moving carousel and the autoplay timer goes off. it jumps unexpectedly.
    const autoplay = emblaMainApi.plugins().autoplay;
    if (!autoplay) return;

    const handlePointerDown = () => autoplay.stop();
    const handleSettle = () => autoplay.play();

    emblaThumbsApi.on('pointerDown', handlePointerDown);
    emblaThumbsApi.on('settle', handleSettle);

    return () => {
      emblaThumbsApi.off('pointerDown', handlePointerDown);
      emblaThumbsApi.off('settle', handleSettle);
    };
  }, [emblaMainApi, emblaThumbsApi]);

  return (
    <BulkErrorBoundary location="Product/ImageGallery">
      <div className="my-3 max-w-3xl mx-auto overflow-hidden">
        <div ref={emblaMainRef} className="relative group">
          <div className="flex gap-4">
            {images.map((image, index) => (
              <div
                className="grow-0 shrink-0 basis-full cursor-pointer"
                key={index}
              >
                <CustomImage image={image} width={1200} height={1200} />
              </div>
            ))}
          </div>
          <div className="absolute inset-0 items-center justify-between px-4 hidden group-hover:flex">
            <button
              type="button"
              className="text-orange hover:scale-120 duration-500"
              onClick={scrollPrev}
            >
              <Icons.Chevron.Left size={50} />
            </button>
            <button
              type="button"
              className="text-orange hover:scale-120 duration-500"
              onClick={scrollNext}
            >
              <Icons.Chevron.Right size={50} />
            </button>
          </div>
        </div>
        <div className="mt-3">
          <div className="overflow-hidden -ml-2" ref={emblaThumbsRef}>
            <div
              className={`flex ${centerTrack ? 'justify-center' : ''}`}
              ref={trackRef}
            >
              {images.map((image, index) => (
                <div
                  key={`thumb-${index}`}
                  // need fallback of basis-1/4 bc mobile should only ever show max 4 images across and then become scrollable
                  // desktop needs dynamic width to always fill hero image width
                  className={mergeStyles(
                    'shrink-0 pl-2 basis-1/4 cursor-pointer',
                    index !== selectedIndex ? 'opacity-50' : ''
                  )}
                >
                  <button
                    onClick={() => onThumbClick(index)}
                    type="button"
                    className="rounded-2xl"
                  >
                    <CustomImage image={image} width={200} height={200} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </BulkErrorBoundary>
  );
}
